import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet} from 'react-helmet';

import {
  gaGoogleRTThunk,
  gaEcomThunk,
  gaBotTrafficClientIdentification,
} from '@stencil/hotels-ga/src/thunks';
import {gaConfig} from '@stencil/hotels-config';

import {
  staticApiRequestSelector,
  hotelDetailsSelector,
  roomsDataSelector,
  multiRoomFlowStatusSelector,
  searchRoomApiRequestSelector,
  isAltAccoSelector,
  alternateDatesRequestSelector,
  alternateDatesSelector,
  searchHotelDetailsSelector,
  uuidSelector,
  isDetailsSeoSelector,
  hostInfoSelector,
  detailsUnifSeoReviewsSelector,
  hotelierMediaSelector,
  hotelNameSelector,
  mediaV2Selector,
  hotelMMTIdSelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

import {
  fetchDetailsStaticDataThunk as fetchDetailsCgStaticDataThunk,
  fetchSearchRoomDataThunk,
  fetchAlternateDatesThunk,
} from '@stencil/hotels-cg-reducer/src/details/thunks';
import {FetchStatusType} from '@stencil/hotels-cg-reducer/src/details/types';

import SearchWidget from '../../components/SearchWidget/SearchWidget';
import RnR from './containers/RnR/RNR';
import SimilarHotels from './containers/SimilarHotels';
import Amenities from './containers/Amenities/Amenities';
import LocationMap from './containers/LocationMap/LocationMap';
import Navigation from './containers/Navigation/Navigation';
import PropertyLayout from './containers/PropertyLayout';
import Policy from './containers/Policy/Policy';
import CaptivateGallery from './containers/CaptivateGallery/CaptivateGallery';
import HostInfo from './containers/HostInfo/HostInfo';
import Faqs from './containers/Faqs/Faqs';

import * as S from './DetailsPage.styles';
import {useLocation, useParams} from 'react-router-dom';
import {
  commonSelectorFn,
  goingForwardPropertyIdSelector,
  pokusExpDataSelectorFn,
  countrySelectorFn,
  hotelsPartnerFn,
  scrollToHotelRoomsSelector,
  isDateUpdatedForUnifSEOSelector,
  fetchPokusDataStatusSelectorFn,
  requestQuerySelectorFn,
  scrollToIdOnBackSelector,
  roomStringSelectorFn,
  checkoutSelectorFn,
  checkinSelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';
import {appConfig} from '@stencil/hotels-config';
import {IUpdate_Query, UPDATE_QUERY} from '@stencil/hotels-common-reducer/src/types';
import {
  checkIsSeoFromSearchString,
  checkUserSelectionInStorage,
  getNewCheckInCheckOutDate,
  isBot,
  isCheckInDateElapsed,
  isNewFLowUrl,
  isObjectEmpty,
  setUserSearchInformationInLocalStorage,
  showHideSeoServerRenderedElement,
} from '@stencil/hotels-utils/src/common-utils';
import {
  resetSelectedProperty,
  updateSelectedProperty,
  updateQueryAction,
  isScrollToHotelRooms,
} from '@stencil/hotels-common-reducer/src/actions';
import {userSelector} from '@stencil/user-reducer';
import parseDetailsPageUrlToConfig from '@stencil/hotels-utils/src/parse-details-page-url-to-config';
import {
  checkIsDateUnderscoreFromUrl,
  checkIsDateUnderscoreFromUrlCg,
  twoDatesAreSame,
} from '@stencil/hotels-utils/src/date-utils/date-utils';
import {
  updateDetailsPageConfigAction,
  updateDetailsSeoFlagAction,
} from '@stencil/hotels-cg-reducer/src/details/actions';
import handlePokusLoad from '@stencil/hotels-hooks/src/handlePokusLoad';
import useIsClient from '@stencil/hotels-hooks/src/useIsClient';
import FoodWidget from './containers/FoodWidget/FoodWidget';
import PropertyHighlights from './containers/PropertyHighlights/PropertyHighlights';
import usePropertyLayout from '@stencil/hotels-hooks/src/usePropertyLayout/usePropertyLayout';
import {
  scrollIntoViewDetails,
  scrollToEl,
} from '@stencil/hotels-utils/src/scroll-into-view-details';
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {readCheckInFromLocalStorageThunk} from '@stencil/hotels-common-reducer/src/thunks';
import LoginBlocker from '../../components/LoginBlocker/LoginBlocker';
import DetailsLoader, {
  DetailsBottomLoader,
} from '../../components/DetailsPageLoader/DetailsPageLoader';
import getComponentRankList, {
  getComponentsCardForSeo,
} from '@stencil/hotels-utils/src/details-page-components-ranking';

//COMPONENTS
import RoomSelection from './containers/RoomSelection/RoomSelection';
import NIDHIBanner from './containers/NIDHIBanner/NIDHIBanner';
import DetailPreviousBooking from './containers/PreviousBooking/PreviousBooking';
import RenderSection from '@stencil/gi-components/src/RenderSection';
import WhatHappenedModal from '@stencil/gi-components/src/WhatHappenedModal/WhatHappenedModal';

// Omniture imports
import {
  resetLoadedComponent,
  resetVisibleComponent,
} from '@stencil/omniture-analytics/src/omniture-utils';
import {CommonPageEvents} from '@stencil/omniture-analytics/src/constants/common';
import {
  DETAIL_API,
  DETAIL_SEO_PAGES,
  getMapiApisToCall,
  getPageName,
  getUnifPageName,
} from '@stencil/hotels-utils/src/seo-utils';
import {metaPartners} from '@stencil/hotels-config/src/config/app-config';
import {omnitureThunk, omnitureConfig, SoldOutType} from '@stencil/omniture-analytics/src';
import DetailsSeoReviews from '../../components/SeoReviews/DetailsSeoReviews';
import AboutHotel from './containers/AboutHotel/AboutHotel';
import HotelOverview from './containers/HotelOverview/HotelOverview';
import {ICheckinCheckoutDate} from '@stencil/hotels-types';
import {parseFilterData} from '@stencil/use-filter/src/filterUtils';
import useQuery from '@stencil/use-query';
import getSrpUrl from '@stencil/hotels-utils/src/get-srp-url';
import {getNewUrl} from '@stencil/hotels-utils/src/get-new-flow-url';

const numItemsHtlDesc = 6;
const numLinesHtlDesc = 3;

const ComponentsMapRef = {
  propertyLayout: PropertyLayout,
  similarHotels: SimilarHotels,
  detailPreviousBooking: DetailPreviousBooking,
  roomSelection: RoomSelection,
  nidhiBanner: NIDHIBanner,
  amenities: Amenities,
  hostInfo: HostInfo,
  foodWidget: FoodWidget,
  rnR: RnR,
  policy: Policy,
  locationMap: LocationMap,
  photosCard: CaptivateGallery,
  popularReviews: DetailsSeoReviews,
  propertyHighlights: PropertyHighlights,
  newFaqs: Faqs,
};

const DetailsPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const {allParams} = useQuery();

  // Cg Selectors
  const scrollToHotelRooms = useSelector(scrollToHotelRoomsSelector);
  const countryCode = useSelector(countrySelectorFn);
  const partner = useSelector(hotelsPartnerFn);
  const uuids = useSelector(uuidSelector);
  const isSeoUnif = useSelector(isDetailsSeoSelector);
  const requestQuery = useSelector(requestQuerySelectorFn);
  const staticApiRequest = useSelector(staticApiRequestSelector);
  const mediaV2 = useSelector(mediaV2Selector);
  const staticHotelDetailsData = useSelector(hotelDetailsSelector);
  const roomData = useSelector(roomsDataSelector);
  const searchRoomApiRequest = useSelector(searchRoomApiRequestSelector);
  const isAltAcoo = useSelector(isAltAccoSelector);
  const alternateDatesRequest = useSelector(alternateDatesRequestSelector);
  const alternateDatesData = useSelector(alternateDatesSelector);
  const searchHotelDetails = useSelector(searchHotelDetailsSelector);
  const {isLoggedIn, setHeaderLoginClick: isHeaderLoginClicked} = useSelector(userSelector);
  const hotelIdInState = useSelector(goingForwardPropertyIdSelector);
  const isDateUpdatedForSEO = useSelector(isDateUpdatedForUnifSEOSelector);
  const hostInfo = useSelector(hostInfoSelector);
  const hotelName = useSelector(hotelNameSelector);
  const seoReviewsData = useSelector(detailsUnifSeoReviewsSelector);
  const scrollToIdOnBack = useSelector(scrollToIdOnBackSelector);
  const roomStringFromStore = useSelector(roomStringSelectorFn);
  const checkoutFromStore = useSelector(checkoutSelectorFn);
  const checkinFromStore = useSelector(checkinSelectorFn);
  const hotelMMTId = useSelector(hotelMMTIdSelector);
  const {
    checkin = {} as ICheckinCheckoutDate,
    checkout = {} as ICheckinCheckoutDate,
    roomString,
    destination,
    country,
    locus,
    selectedHotel,
  } = useSelector(commonSelectorFn);

  const pokusExpData = useSelector(pokusExpDataSelectorFn);
  const pokusDataStatus = useSelector(fetchPokusDataStatusSelectorFn);
  const allHotelierMedia = useSelector(hotelierMediaSelector);
  const multiRoomFlowStatus = useSelector(multiRoomFlowStatusSelector);
  const isDateUnderscoreFromUrl = checkIsDateUnderscoreFromUrlCg(allParams);

  const hasPageViewFired = useRef(false);
  const isSeoRef = useRef(isSeoUnif);

  const [searchButtonClicked, setSearchButtonClicked] = useState(false);
  const [isPokusLoaded, setIsPokusLoaded] = useState(false);
  const [seoSoldoutredirectFlow, setSeoSoldoutredirectFlow] = useState(false);
  const [shouldRedirectToGoSuggest, setShouldRedirectToGoSuggest] = useState(false);
  const [calendarMode, setCalendarMode] = useState('');

  const propertyLayoutProps = usePropertyLayout();
  const isClient = useIsClient();

  const isSoldOut = useMemo(() => !roomData || !roomData?.length, [roomData]);
  const pageNameSlug = getUnifPageName(location?.search, params, requestQuery?.pageType);
  const pageName = getPageName(pageNameSlug);
  const apisToCall = getMapiApisToCall(pageNameSlug, false);

  const isMultiRoom = multiRoomFlowStatus?.isMultiRoomFlow;
  const isSeoDetailPage = isSeoUnif && pageName === DETAIL_SEO_PAGES.details;
  const {premiumUsp = {}, propertyType = ''} = staticHotelDetailsData || {};

  const isSeoFromSearch = checkIsSeoFromSearchString(location.search);
  const channel = isSeoFromSearch ? appConfig.channelSource.SEO : appConfig.channelSource.DIRECT;
  handlePokusLoad('desktop', channel);

  const pushOmnitureThunk = useCallback(
    (additionalData = {}) => {
      return dispatch(
        omnitureThunk(
          omnitureConfig.pages.DetailsUnif,
          omnitureConfig.events.pageView,
          additionalData,
        ),
      );
    },
    [dispatch],
  );

  const updateQueryCaller = useCallback((actionData: IUpdate_Query['actionData']) => {
    return dispatch({type: UPDATE_QUERY, actionData});
  }, []);

  const parsedConfig = useMemo(
    () => parseDetailsPageUrlToConfig(location.search, params),
    [
      location?.search,
      params?.hotelNameSlug,
      params?.hotelId,
      params?.cityName,
      params?.hotelQuery,
    ],
  );
  const {hotelId} = parsedConfig;
  const unifFilterData = parseFilterData(parsedConfig?.unifFilterData);

  const srpRedirectUrl = useMemo(() => {
    const isNewFlowOpen = isNewFLowUrl(location?.pathname, partner);
    const haveLocus = !!(locus?.id && locus?.type);
    const mmtId = selectedHotel?.mmtId || hotelMMTId;

    const isNewDetailsFlow = haveLocus && isNewFlowOpen;

    let finalDestination = {...destination, mmtId: mmtId};
    let finalLocus = {...locus, search: 'hotel', mmtId: mmtId};
    const data = {
      city: allParams?.city || '',
      searchText: '',
      checkin,
      checkout,
      roomString,
      destination: finalDestination,
      locus: finalLocus,
      country,
      partner,
      mmtId: mmtId,
      propertyName: selectedHotel?.propertyName,
      funnel: 'main',
    };
    const srpUrl = isNewDetailsFlow ? getNewUrl(data, 'srp') : getSrpUrl(data);

    return srpUrl;
  }, [
    hotelId,
    checkin,
    checkout,
    roomString,
    destination,
    partner,
    country,
    locus,
    allParams,
    selectedHotel,
    hotelMMTId,
  ]);

  const searchRoomSuccessCallback = useCallback(() => {
    dispatch(gaGoogleRTThunk('details'));
    dispatch(gaBotTrafficClientIdentification('details'));
    dispatch(gaEcomThunk(gaConfig.ecommerceEvents.detailsPageLanding));
  }, [dispatch, gaGoogleRTThunk, gaBotTrafficClientIdentification, gaEcomThunk]);

  const onDateandPaxChange = (newCheckInDate, newCheckoutDate, newPaxString) => {
    dispatch(
      updateQueryAction({
        checkin: newCheckInDate,
        checkout: newCheckoutDate,
        roomString: newPaxString,
        isDateUpdatedForSEO: true,
      } as IUpdate_Query['actionData']),
    );
  };

  useEffect(() => {
    if (
      pokusDataStatus === FetchStatusType.RESOLVED ||
      pokusDataStatus === FetchStatusType.REJECTED
    ) {
      setIsPokusLoaded(true);
    }
  }, [pokusDataStatus]);

  useEffect(() => {
    if (staticHotelDetailsData && searchRoomApiRequest?.success) {
      if (!hasPageViewFired.current) {
        hasPageViewFired.current = true;
        const omniAdditionalData = {};
        if (isSoldOut) {
          omniAdditionalData.error = `${SoldOutType.Detail}|${searchRoomApiRequest?.error?.code}|${searchRoomApiRequest?.error?.message}`;
        }
        pushOmnitureThunk(omniAdditionalData);
      }
    }
    if (
      !searchRoomApiRequest?.loading &&
      seoSoldoutredirectFlow &&
      isSoldOut &&
      isSeoRef.current !== isSeoUnif &&
      !isSeoUnif
    ) {
      setShouldRedirectToGoSuggest(true);
    } else if (!searchRoomApiRequest?.loading && seoSoldoutredirectFlow && !isSoldOut) {
      setSeoSoldoutredirectFlow(false);
      isSeoRef.current = false;
    } else if (!searchRoomApiRequest?.loading && isSeoUnif && isSoldOut) {
      setCalendarMode('checkin');
      setSeoSoldoutredirectFlow(true);
    }
  }, [staticHotelDetailsData, seoSoldoutredirectFlow, searchRoomApiRequest, isSoldOut]);

  useEffect(() => {
    dispatch(
      updateDetailsPageConfigAction({
        actionData: parsedConfig,
      }),
    );
    window.scrollTo(0, 0);
    return () => {
      dispatch(resetSelectedProperty());
      dispatch(
        updateSelectedProperty({
          payload: {
            propertyName: '',
            propertyId: '',
            mmtId: '',
          },
        }),
      );
      /* @Note this resets loaded components and impressions on page load*/
      resetLoadedComponent();
      resetVisibleComponent();
      showHideSeoServerRenderedElement(false);
    };
  }, []);

  useEffect(() => {
    dispatch(
      updateDetailsSeoFlagAction({
        isSeo: location?.search ? checkIsSeoFromSearchString(location.search) : isSeoUnif,
      }),
    );
  }, [location.search]);

  useEffect(() => {
    if (scrollToHotelRooms && roomData) {
      scrollIntoViewDetails(
        document.querySelector(
          isAltAcoo
            ? detailsNavigationIdsAndValues.stays.id
            : detailsNavigationIdsAndValues.rooms.id,
        ),
      );
      dispatch(isScrollToHotelRooms(false));
    }
    if (scrollToIdOnBack) {
      scrollToEl(scrollToIdOnBack);
    }
  }, [scrollToHotelRooms, roomData, scrollToIdOnBack]);

  useEffect(() => {
    if (isSeoUnif || parsedConfig?.altAcco) {
      showHideSeoServerRenderedElement(isSeoUnif);
      dispatch(
        readCheckInFromLocalStorageThunk({
          isDetail: true,
          setCalendarMode,
          isAltAcco: parsedConfig?.altAcco,
        }),
      );
    }
  }, [isSeoUnif, parsedConfig?.altAcco]);

  useEffect(() => {
    const isCheckInDateNotElapsed = isCheckInDateElapsed(checkin);
    const isBotAgent = isBot(window?.navigator?.userAgent);

    if (!isCheckInDateNotElapsed) {
      const {defaultCheckin, defaultCheckout} = getNewCheckInCheckOutDate();
      updateQueryCaller({
        checkin: {...checkin, ...defaultCheckin},
        checkout: {...checkout, ...defaultCheckout},
      });
      setCalendarMode('checkin');
    }
    async function fetchNeededDetailsData() {
      if (
        !staticApiRequest?.loading &&
        !staticApiRequest?.error &&
        (!staticHotelDetailsData || (uuids?.length && isSeoUnif))
      ) {
        await dispatch(fetchDetailsCgStaticDataThunk('desktop', null, parsedConfig?.mmtId, uuids));
      }
      if (staticHotelDetailsData) {
        dispatch(
          updateSelectedProperty({
            payload: {
              propertyName: staticHotelDetailsData?.name,
              propertyId: parsedConfig?.hotelId,
              mmtId: staticHotelDetailsData?.id || parsedConfig?.mmtId,
            },
          }),
        );
        if (!destination?._id || !destination?.n) {
          dispatch(
            updateQueryAction({
              destination: {
                _id:
                  parsedConfig?.selectedHotelStatic?.vcid ||
                  staticHotelDetailsData?.locationDetail?.id ||
                  '',
                n: staticHotelDetailsData?.locationDetail?.name || '',
                t: 'city',
              },
              locus: parsedConfig.locus,
              country: staticHotelDetailsData?.locationDetail?.countryId,
            }),
          );
        }
      }

      if (
        !searchRoomApiRequest?.loading &&
        !searchRoomApiRequest?.success &&
        !roomData?.length &&
        !isBotAgent
      ) {
        await dispatch(
          fetchSearchRoomDataThunk(
            'desktop',
            null,
            searchRoomSuccessCallback,
            isSeoUnif,
            unifFilterData,
            parsedConfig?.mmtId,
          ),
        );
      }
    }
    if (isPokusLoaded || (isHeaderLoginClicked && isPokusLoaded)) {
      fetchNeededDetailsData();
    }
  }, [staticHotelDetailsData, roomData, isPokusLoaded, isHeaderLoginClicked]);

  useEffect(() => {
    if (searchButtonClicked) {
      setSearchButtonClicked(false);
      return;
    }

    const linkNotMatchesConfig =
      hotelIdInState !== parsedConfig?.hotelId ||
      !twoDatesAreSame(checkin?.date, parsedConfig?.checkin?.date) ||
      !twoDatesAreSame(checkout?.date, parsedConfig?.checkout?.date) ||
      roomString !== parsedConfig?.roomString;

    async function fetchNeededDetailsData() {
      await dispatch(
        fetchDetailsCgStaticDataThunk('desktop', parsedConfig?.hotelId, parsedConfig?.mmtId),
      );
    }
    if (isDateUnderscoreFromUrl) {
      checkUserSelectionInStorage(checkin, checkout, roomString, onDateandPaxChange, 1, 'dweb');
    } else if (linkNotMatchesConfig) {
      if (isSeoUnif) {
        const {defaultCheckin: newCheckInDate, defaultCheckout: newCheckOutDate} =
          getNewCheckInCheckOutDate(25, 26, true);

        let newDateObj = {
          checkin: {...checkin, ...newCheckInDate},
          checkout: {...checkout, ...newCheckOutDate},
        };

        updateQueryCaller(newDateObj);
      } else {
        updateQueryCaller({
          checkin: {...checkin, ...parsedConfig.checkin},
          checkout: {...checkout, ...parsedConfig.checkout},
          roomString: parsedConfig.roomString || roomString,
        });
      }
      if (isPokusLoaded) fetchNeededDetailsData();
    }
  }, [
    parsedConfig.hotelId,
    parsedConfig.checkin.date,
    parsedConfig.checkout.date,
    parsedConfig.roomString,
    isPokusLoaded,
  ]);

  useEffect(() => {
    if (isDateUpdatedForSEO) {
      dispatch(
        fetchSearchRoomDataThunk(
          'desktop',
          parsedConfig?.hotelId,
          searchRoomSuccessCallback,
          isSeoUnif,
          unifFilterData,
          parsedConfig?.mmtId,
        ),
      );
    }
  }, [isDateUpdatedForSEO]);

  useEffect(() => {
    if (staticHotelDetailsData) {
      if (countryCode !== 'IN' && partner !== 'hotels-international') {
        dispatch(
          updateQueryAction({
            partner: 'hotels-international',
          }),
        );
      } else if (countryCode == 'IN' && partner == 'hotels-international') {
        dispatch(
          updateQueryAction({
            partner: 'hotels',
          }),
        );
      }
    }
  }, [staticHotelDetailsData, partner, countryCode]);

  useEffect(() => {
    if (staticHotelDetailsData && searchHotelDetails?.voyagerCityId) {
      dispatch(
        updateQueryAction({
          destination: {
            _id: searchHotelDetails?.voyagerCityId,
            n: staticHotelDetailsData?.locationDetail?.name || '',
          },
        }),
      );
    }
  }, [staticHotelDetailsData, searchHotelDetails]);

  useEffect(() => {
    if (
      staticHotelDetailsData?.id &&
      !searchRoomApiRequest?.loading &&
      isSoldOut &&
      !alternateDatesRequest?.loading &&
      !alternateDatesRequest?.error &&
      !alternateDatesRequest?.success &&
      (!alternateDatesData || !alternateDatesData?.length)
    ) {
      dispatch(fetchAlternateDatesThunk({}));
    }
  }, [staticHotelDetailsData?.id, searchRoomApiRequest?.loading, isSoldOut, alternateDatesRequest]);

  useEffect(() => {
    if (!(isSeoUnif || parsedConfig?.altAcco || isDateUnderscoreFromUrl)) {
      setUserSearchInformationInLocalStorage(
        checkinFromStore,
        checkoutFromStore,
        roomStringFromStore,
      );
    }
  }, [parsedConfig?.altAcco, isSeoUnif, isDateUnderscoreFromUrl]);

  const componentsRankList = useMemo(() => {
    if (isSeoUnif && pageNameSlug) {
      return getComponentsCardForSeo({pageName: pageNameSlug, isDweb: true});
    }
    if (isSeoUnif) {
      return getComponentRankList({isSeo: isSeoUnif, isDweb: true});
    }
    if (searchRoomApiRequest?.success) {
      const isStarRating = staticHotelDetailsData?.starRating > 3;
      return getComponentRankList({isSoldOut, isAltAcoo, isStarRating, pokusExpData, isDweb: true});
    }
    return [];
  }, [
    pokusExpData,
    searchRoomApiRequest,
    isSeoUnif,
    isSoldOut,
    isAltAcoo,
    staticHotelDetailsData?.starRating,
  ]);

  const handlOpenCalendar = useCallback(
    val => {
      dispatch(
        omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
          cta: {
            name: CommonPageEvents.checkAvailabilityClick,
            type: omnitureConfig.ctaType.Card,
            componentName: CommonPageEvents.checkAvailabilityClick,
            destination: ``,
          },
        }),
      );
      setCalendarMode(val || 'checkin');
    },
    [setCalendarMode],
  );

  const ConditionMap = {
    similarHotels: {renderCondition: !isMultiRoom},
    detailPreviousBooking: {renderCondition: !isMultiRoom},
    roomSelection: {props: {showHeading: isSeoDetailPage, handlOpenCalendar: handlOpenCalendar}},
    hostInfo: {renderCondition: !isObjectEmpty(hostInfo) && !!hostInfo?.about, props: {hostInfo}},
    photosCard: {props: {isSeo: !isSeoDetailPage && isSeoUnif}},
    popularReviews: {renderCondition: isSeoDetailPage, props: {seoReviewsData}},
    propertyHighlights: {
      renderCondition: !!premiumUsp?.highlights?.length && numItemsHtlDesc > 0,
      props: {
        premiumUsp,
        propertyType,
        numItemsHtlDesc,
        numLinesHtlDesc,
      },
    },
    rnR: {props: {isSeo: isSeoUnif, travellerImpressions: pokusExpData?.TravellerImpressions}},
    propertyLayout: {
      renderCondition: !!propertyLayoutProps?.isPropertyLayoutExist,
      props: propertyLayoutProps,
    },
  };

  if (!isSeoUnif && staticApiRequest?.loading) {
    console.log('pagename...', pageName);
    return (
      <S.LoaderContainer>
        <DetailsLoader />
      </S.LoaderContainer>
    );
  }

  const renderDetailsPage = () => (
    <>
      {!isSeoUnif && (
        <Helmet title={hotelName} meta={[{name: 'robots', content: 'noindex, nofollow'}]} />
      )}
      {isSeoUnif && (
        <S.SeoBreadCrumbContainer
          id="seoBreadCrumbDontRemoveContainer"
          suppressHydrationWarning={true}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: isClient ? window?.hotelsseocms?.breadcrumb : '',
          }}
        />
      )}
      {shouldRedirectToGoSuggest && (
        <WhatHappenedModal
          title="This property is soldout for these dates"
          redirect={{
            shouldRedirect: true,
            to: srpRedirectUrl,
            after: 3,
            displayText: `More Hotels in ${destination?.n}`,
          }}
        />
      )}
      <HotelOverview
        isSeoDetailPage={isSeoDetailPage}
        isSeoUnif={isSeoUnif}
        pageName={pageName}
        isSoldOut={isSoldOut}
        pokusExpData={pokusExpData}
        isPokusLoaded={isPokusLoaded}
        setCalendarMode={setCalendarMode}
        requestQuery={requestQuery}
        handlOpenCalendar={handlOpenCalendar}
      />
      {!isSeoUnif && !componentsRankList?.length && !searchRoomApiRequest?.success ? (
        <DetailsBottomLoader />
      ) : (
        <>
          <Navigation isSeo={!isSeoDetailPage && isSeoUnif} />
          {isSeoDetailPage && <AboutHotel />}
          {componentsRankList?.map(item => (
            <RenderSection
              componentsMap={ComponentsMapRef}
              conditionMap={ConditionMap}
              componentName={item}
            />
          ))}
        </>
      )}
    </>
  );

  return (
    <>
      <S.DetailCardInnerSection itemScope itemType="http://schema.org/Hotel">
        <SearchWidget
          mode={calendarMode}
          setParentCalMode={setCalendarMode}
          isDetailsPage
          openNewTabs={true}
          updateSearchClicked={() => setSearchButtonClicked(true)}
          isSeoDetail={isSeoUnif}
          hotelId={hotelId}
          appliedFilters={unifFilterData}
        />
        {renderDetailsPage()}
      </S.DetailCardInnerSection>
      <LoginBlocker source="details" />
    </>
  );
};

export default DetailsPage;
